import "./NotificationsDrawer.css";
import { Drawer } from "antd";
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import logo from "../assets/Numente-logo.svg";

const baseURL = `${process.env.REACT_APP_BASE_URL}/`;

const NotificationsDrawer = ({
  onClose,
  open,
  providerId,
  setNotificationCount,
}) => {
  const [filterValue, setFilterValue] = useState("unread");
  const [lastFetchTime, setLastFetchTime] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notifications, setNotifications] = useState([]);

  const getAllNotifications = async (forceRefresh = false) => {
    if (!providerId) return;

    if (!forceRefresh && lastFetchTime && Date.now() - lastFetchTime < 5000)
      return;

    setLastFetchTime(Date.now());
    setLoading(true);

    try {
      const res = await axios.get(
        `${baseURL}provider-notifications/${providerId}`
      );

      if (Array.isArray(res.data.data)) {
        setNotifications((prev) => {
          if (prev.length === 0 || forceRefresh) {
            return res.data.data.sort(
              (a, b) => new Date(b.sentAt) - new Date(a.sentAt)
            );
          }

          const existingIds = new Set(prev.map((n) => n.id));
          const newNotifications = res.data.data.filter(
            (n) => !existingIds.has(n.id)
          );

          if (newNotifications.length === 0) return prev;

          const merged = [...newNotifications, ...prev];

          return merged.sort((a, b) => new Date(b.sentAt) - new Date(a.sentAt));
        });
      }
    } catch (err) {
      console.error("Failed to fetch notifications:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (providerId) getAllNotifications(true);
  }, [providerId]);

  useEffect(() => {
    if (open && providerId) getAllNotifications();
  }, [open, providerId]);

  const unreadCount = useMemo(() => {
    return notifications.filter((n) => !n.isRead).length;
  }, [notifications]);

  useEffect(() => {
    setNotificationCount(unreadCount);
  }, [unreadCount]);

  useEffect(() => {
    const handleFcmNotification = (e) => {
      const newNotification = e.detail;

      setNotifications((prev) => {
        const exists = prev.some((n) => n.id === newNotification.id);

        if (exists) return prev;

        const notificationWithTimestamp = {
          ...newNotification,
          isRead: false,
          sentAt: newNotification.sentAt || new Date().toISOString(),
        };

        return [notificationWithTimestamp, ...prev].sort(
          (a, b) => new Date(b.sentAt) - new Date(a.sentAt)
        );
      });
    };

    window.addEventListener("fcm-notification", handleFcmNotification);
    return () => {
      window.removeEventListener("fcm-notification", handleFcmNotification);
    };
  }, []);

  const filteredNotifications = useMemo(() => {
    if (filterValue === "unread") return notifications.filter((n) => !n.isRead);
    if (filterValue === "read") return notifications.filter((n) => n.isRead);

    return notifications;
  }, [filterValue, notifications]);

  const groupedNotifications = useMemo(() => {
    const last30Days = [];
    const last7Days = [];
    const now = new Date();
    const today = [];

    filteredNotifications.forEach((notif) => {
      const notifDate = new Date(notif.sentAt);
      const diffInDays = Math.floor((now - notifDate) / (1000 * 60 * 60 * 24));
      const diffInHours = Math.floor((now - notifDate) / (1000 * 60 * 60));

      if (diffInHours < 24) {
        today.push(notif);
      } else if (diffInDays <= 7) {
        last7Days.push(notif);
      } else if (diffInDays <= 30) {
        last30Days.push(notif);
      }
    });

    return { last30Days, last7Days, today };
  }, [filteredNotifications]);

  const formatRelativeTime = (date, section) => {
    const now = new Date();

    const diffMs = now - date;

    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffWeeks = Math.floor(diffDays / 7);

    if (section === "today") {
      if (diffMinutes < 1) return "Just now";
      if (diffMinutes < 60) return `${diffMinutes}m`;
      return `${diffHours}h ago`;
    }
    if (section === "last7") return `${diffDays}d ago`;
    if (section === "last30") return `${diffWeeks}w ago`;

    return "";
  };

  const handleNotificationClick = async (notif) => {
    try {
      await axios.put(
        `${baseURL}provider-notifications/mark-read/${notif.id}`,
        { providerId }
      );

      setNotifications((prev) =>
        prev.map((n) => (n.id === notif.id ? { ...n, isRead: true } : n))
      );
    } catch (err) {
      console.error("Failed to mark notification as read:", err);
    }
  };

  const handleReadAllNotifications = async () => {
    try {
      await axios.put(`${baseURL}provider-notifications/mark-all-read`, {
        providerId,
      });

      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
    } catch (err) {
      console.error("Failed to mark all notifications as read:", err);
    }
  };

  const handleClearAllNotifications = async () => {
    try {
      await axios.put(`${baseURL}provider-notifications/clear-all`, {
        providerId,
      });

      setNotifications([]);
    } catch (err) {
      console.error("Failed to clear all notifications:", err);
    }
  };

  const renderNotifications = (list, sectionType) =>
    list.map((notif) => (
      <div
        className="notification-item"
        key={notif.id}
        onClick={() => handleNotificationClick(notif)}
        style={{ backgroundColor: notif.isRead ? "transparent" : "#EAEAEAB2" }}>
        <div
          style={{
            alignItems: "center",
            display: "flex",
            justifyContent: "center",
          }}>
          <div className="notification-indicator">
            <img alt="logo" height={48} src={logo} width={48} />
          </div>

          <div className="notification-content">
            <div className="notification-title">{notif.title}</div>

            <div className="notification-description">{notif.body}</div>
          </div>
        </div>

        <div className="notification-actions">
          <span className="notification-time">
            {formatRelativeTime(new Date(notif.sentAt), sectionType)}
          </span>

          <div className="notification-menu-button">
            <span className="notification-menu-icon">⋯</span>
          </div>
        </div>
      </div>
    ));

  return (
    <Drawer
      closable={false}
      onClose={onClose}
      open={open}
      placement="right"
      styles={{ body: { padding: "16px" }, header: { padding: "16px 32px" } }}
      title={
        <div className="drawer-header">
          <div className="drawer-header-col1">
            <span className="notification-heading">Notifications</span>

            <div className="notifications-header">
              <select
                className="form-select filter-select"
                onChange={(e) => setFilterValue(e.target.value)}
                value={filterValue}>
                <option value="all">All</option>

                <option value="read">Read</option>

                <option value="unread">Unread</option>
              </select>
            </div>
          </div>

          <div className="drawer-header-col2" style={{ gap: "2px" }}>
            <button
              className="action-btn secondary-btn"
              disabled={loading}
              onClick={() => getAllNotifications(true)}
              style={{}}>
              {loading ? "Refreshing..." : "Refresh"}
            </button>

            <button
              className="action-btn red-btn"
              onClick={handleClearAllNotifications}
              style={{}}>
              Clear all
            </button>

            {filteredNotifications.length > 0 && (
              <button
                className="action-btn blue-btn"
                onClick={handleReadAllNotifications}
                style={{}}>
                Mark all as read
              </button>
            )}
          </div>
        </div>
      }
      width={626}>
      <div className="notifications-drawer-container">
        {loading && notifications.length === 0 ? (
          <div className="text-center" style={{ padding: "20px" }}>
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>

            <p className="mt-2">Loading notifications...</p>
          </div>
        ) : filteredNotifications.length > 0 ? (
          <>
            {groupedNotifications.today.length > 0 && (
              <div className="section-container">
                <h5 className="section-title">Today</h5>

                {renderNotifications(groupedNotifications.today, "today")}
              </div>
            )}

            {groupedNotifications.last7Days.length > 0 && (
              <div className="section-container">
                <h5 className="section-title">Last 7 Days</h5>

                {renderNotifications(groupedNotifications.last7Days, "last7")}
              </div>
            )}

            {groupedNotifications.last30Days.length > 0 && (
              <div className="section-container">
                <h5 className="section-title">Last 30 Days</h5>

                {renderNotifications(groupedNotifications.last30Days, "last30")}
              </div>
            )}
          </>
        ) : (
          <p className="fw-semibold text-center">
            You're all caught up. New updates will appear here.
          </p>
        )}
      </div>
    </Drawer>
  );
};

const NotificationIndicator = () => (
  <svg
    fill="none"
    height="49"
    viewBox="0 0 48 49"
    width="48"
    xmlns="http://www.w3.org/2000/svg">
    <g clipPath="url(#clip0_11167_184)">
      <rect fill="#064260" height="48" width="48" y="0.5" />
    </g>

    <defs>
      <clipPath id="clip0_11167_184">
        <rect fill="white" height="48" rx="12.6316" width="48" y="0.5" />
      </clipPath>
    </defs>
  </svg>
);

export default NotificationsDrawer;
